/**
 * Quotes - the module's own API (R28), at /api/m/quotes/.
 *
 * Every route carries the channel id; the platform's guard admits the
 * channel's owner, a ChannelPermission holder or a super admin (401 without a
 * token, 404 for a channel the caller cannot see). Who may run !quote /
 * !addquote / !delquote in chat is NOT here: that is the command platform's
 * per-channel override (the Commands page).
 */

import api from '@/api/client'

const BASE = '/api/m/quotes'

export interface Quote {
  id: number
  quote_number: number
  text: string
  attribution: string | null
  added_by: { twitch_id: string; username: string }
  /** The channel's Twitch category when the quote was added. */
  game: string | null
  category: string | null
  created_at: string | null
}

export interface QuoteList {
  quotes: Quote[]
  /** How many match, across all pages. */
  total: number
  limit: number
  offset: number
}

/**
 * A new quote. Leave `game` OUT to have the server look up the channel's
 * current category; send it (even '') to use it as given.
 */
export interface QuoteCreate {
  text: string
  attribution?: string | null
  game?: string | null
}

/** Only the keys present change; null or '' clears attribution or game. */
export interface QuotePatch {
  text?: string
  attribution?: string | null
  game?: string | null
}

export interface QuoteExport {
  channel_id: number
  quotes: Array<Omit<Quote, 'added_by'> & { channel_id: number; added_by: Quote['added_by'] }>
}

export const quotesApi = {
  list: (channelId: number, opts: { search?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams()
    if (opts.search) q.set('search', opts.search)
    q.set('limit', String(opts.limit ?? 20))
    q.set('offset', String(opts.offset ?? 0))
    return api.get<QuoteList>(`${BASE}/${channelId}?${q}`)
  },

  add: (channelId: number, data: QuoteCreate) => api.post<Quote>(`${BASE}/${channelId}`, data),

  update: (channelId: number, number: number, data: QuotePatch) =>
    api.patch<Quote>(`${BASE}/${channelId}/${number}`, data),

  remove: (channelId: number, number: number) =>
    api.delete<{ status: string; quote_number: number }>(`${BASE}/${channelId}/${number}`),

  /**
   * Every quote as JSON. Fetched through the shared client (a plain link
   * would not carry the JWT), then saved by `downloadJson`.
   */
  exportAll: (channelId: number) => api.get<QuoteExport>(`${BASE}/${channelId}/export`),
}

/** Save `data` as a .json file in the browser. */
export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
